import { useState } from "react";
import { ShieldCheck, ShieldAlert, Languages, Gauge, Mic, AlertTriangle, RefreshCw, UserPlus } from "lucide-react";

const LANG_LABEL = { te: "Telugu", ta: "Tamil", hi: "Hindi", kn: "Kannada", ml: "Malayalam", en: "English", other: "Other", unknown: "Unknown" };
const REASON_LABEL = {
  repetition: "Repetition loops", lang_mismatch: "Wrong language",
  hallucination: "Hallucinated filler", low_confidence: "Low confidence",
  garbled: "Garbled text", low_rate: "Abnormal speech rate", speech_rate_out_of_range: "Abnormal speech rate",
};
const GAP_COLOR = { transcribed: "#6C5FBC", repaired: "#B06A2C", excluded: "#B04A66", no_speech: "#CFC9DC" };

function pct(n) { return n == null ? "—" : `${n}%`; }

export default function QualityReport({ report, onRetranscribe }) {
  const [busy, setBusy] = useState(false);
  if (!report) return null;

  const gate = report.gate_passed !== false;
  const counts = report.counts || {};
  const total = counts.total || 0;
  const reasons = report.flagged_by_reason || {};
  const gaps = report.gap_map || [];
  const dur = report.duration_seconds || gaps.reduce((m, g) => Math.max(m, g.end || 0), 0) || 1;
  const prompts = report.speaker_prompts || [];

  async function handleRetranscribe() {
    if (!onRetranscribe) return;
    setBusy(true);
    try { await onRetranscribe(); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4" data-testid="quality-report">
      {/* Gate banner */}
      <div
        data-testid="quality-banner"
        className={"rounded-2xl border p-4 flex items-start gap-3 " +
          (gate ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/70")}
      >
        {gate
          ? <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={1.8} />
          : <ShieldAlert className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" strokeWidth={1.8} />}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            {gate ? "Transcript quality verified" : "Transcript quality low"}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {gate
              ? "Analytics and minutes are built only from validated speech."
              : "Analytics and the Health Score are suppressed until the transcript is re-processed."}
            {!gate && report.gate_reasons?.length > 0 && (
              <span className="ml-1">({report.gate_reasons.join(", ")})</span>
            )}
          </div>
        </div>
        {!gate && onRetranscribe && (
          <button
            data-testid="quality-retranscribe-btn"
            onClick={handleRetranscribe}
            disabled={busy}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-rose-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-rose-700 disabled:opacity-60 transition-colors"
          >
            <RefreshCw className={"w-3.5 h-3.5 " + (busy ? "animate-spin" : "")} strokeWidth={2} />
            {busy ? "Starting…" : "Re-transcribe"}
          </button>
        )}
      </div>

      {/* Metrics card */}
      <div className="yl-surface p-6" data-testid="quality-report-card">
        <div className="eyebrow mb-4">Transcript quality report</div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Metric icon={Gauge} label="Speech coverage" value={pct(report.speech_coverage_pct)} testid="quality-coverage" />
          <Metric icon={Mic} label="Words / min" value={report.words_per_minute ?? "—"} testid="quality-wpm" />
          <Metric icon={ShieldCheck} label="Avg confidence" value={report.avg_confidence != null ? report.avg_confidence : "—"} testid="quality-confidence" />
          <Metric icon={Languages} label="Segments" value={total} testid="quality-segments" />
        </div>

        {/* Verdict counts */}
        <div className="mt-5 flex flex-wrap gap-2 text-[11px]" data-testid="quality-counts">
          <Pill tone="sage" label={`${counts.ok || 0} clean`} />
          {counts.repaired > 0 && <Pill tone="apricot" label={`${counts.repaired} repaired`} />}
          {counts.flagged > 0 && <Pill tone="apricot" label={`${counts.flagged} flagged`} />}
          {counts.excluded > 0 && <Pill tone="blush" label={`${counts.excluded} excluded`} />}
        </div>

        {/* Languages */}
        {report.languages?.length > 0 && (
          <div className="mt-5">
            <div className="text-xs text-muted-foreground mb-2">Languages detected</div>
            <div className="flex flex-wrap gap-2">
              {(report.languages || []).map((l, i) => (
                <span key={i} className="tone-lavender rounded-full px-3 py-1 text-xs font-semibold" data-testid={`quality-lang-${l.lang}`}>
                  {LANG_LABEL[l.lang] || l.lang} · {l.pct}%
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Flagged-by-reason */}
        {Object.keys(reasons).length > 0 && (
          <div className="mt-5">
            <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.8} /> Issues caught
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(reasons).map(([k, v]) => (
                <span key={k} className="rounded-full border border-border px-3 py-1 text-xs">
                  {REASON_LABEL[k] || k} · {v}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Gap map */}
        {gaps.length > 0 && (
          <div className="mt-6" data-testid="quality-gap-map">
            <div className="text-xs text-muted-foreground mb-2">Coverage timeline</div>
            <div className="flex h-3 w-full rounded-full overflow-hidden bg-secondary">
              {gaps.map((g, i) => {
                const w = Math.max(0.3, ((g.end - g.start) / dur) * 100);
                return <div key={i} title={`${g.kind} · ${Math.round(g.start)}–${Math.round(g.end)}s`} style={{ width: `${w}%`, backgroundColor: GAP_COLOR[g.kind] || "#CFC9DC" }} />;
              })}
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-muted-foreground">
              {Object.entries(GAP_COLOR).map(([k, c]) => (
                <span key={k} className="inline-flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c }} /> {k.replace("_", " ")}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Speaker prompts */}
        {prompts.length > 0 && (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="quality-speaker-prompts">
            <div className="text-xs font-semibold flex items-center gap-1.5 text-amber-800">
              <UserPlus className="w-3.5 h-3.5" strokeWidth={1.8} /> Unresolved speakers
            </div>
            <div className="text-xs text-amber-800/80 mt-1">
              {prompts.map((p, i) => (
                <span key={i}>
                  {p.speaker} spoke {Math.round(p.validated_seconds)}s — map them in the Transcript tab{i < prompts.length - 1 ? "; " : "."}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl bg-secondary/40 p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] uppercase tracking-wide">
        {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />} {label}
      </div>
      <div className="mt-2 font-heading text-2xl">{value}</div>
    </div>
  );
}

function Pill({ tone, label }) {
  return <span className={`tone-${tone} rounded-full px-3 py-1 font-semibold`}>{label}</span>;
}
